import { CreateOrganizationProductRequest, FixedPriceSettings, GetOrganizationProductResponse, GetPublicOrganizationProductResponse, PaymentTargetType, ProductPeriod, RecurringPriceSettings } from '../../types';
import { get, post, put, throwHttpError } from './api';

const API_BASE_URL = '/api';

// Allowed currencies for product creation
export const ALLOWED_CURRENCIES = [
    { code: 'usd', name: 'US Dollar (USD)' },
    { code: 'eur', name: 'Euro (EUR)' },
    { code: 'gbp', name: 'British Pound (GBP)' },
    { code: 'aud', name: 'Australian Dollar (AUD)' },
    { code: 'cad', name: 'Canadian Dollar (CAD)' },
    { code: 'mxn', name: 'Mexican Peso (MXN)' },
    { code: 'zar', name: 'South African Rand (ZAR)' },
];

/// Product Creation and Management APIs

export async function createOrganizationProduct(
    organization_id: string,
    body: CreateOrganizationProductRequest,
    organizationKey?: string
): Promise<GetOrganizationProductResponse> {
    const params = new URLSearchParams();
    if (organizationKey) {
        params.append('organization_key', organizationKey);
    }

    const url = `${API_BASE_URL}/product/${organization_id}?${params.toString()}`;

    const response = await fetch(url, post(body));

    if (!response.ok) {
        throw new Error(`Failed to create product: ${response.statusText}`);
    }

    return await response.json();
}

export async function updateOrganizationProduct(
    organization_id: string,
    body: CreateOrganizationProductRequest,
    organizationKey?: string
): Promise<GetOrganizationProductResponse> {
    const params = new URLSearchParams();

    if (organizationKey) {
        params.append('organization_key', organizationKey);
    }
    params.append('product_id', body._id?.$oid ?? '');

    const url = `${API_BASE_URL}/product/${organization_id}?${params.toString()}`;

    const response = await fetch(url, put(body));

    if (!response.ok) {
        throw new Error(`Failed to update product: ${response.statusText}`);
    }

    return await response.json();
}

export async function archiveOrganizationProduct(
    organization_id: string,
    product_id: string,
    organizationKey?: string
): Promise<void> {
    const params = new URLSearchParams();

    if (organizationKey) {
        params.append('organization_key', organizationKey);
    }
    params.append('product_id', product_id);

    const url = `${API_BASE_URL}/product/${organization_id}/archive?${params.toString()}`;

    const response = await fetch(url, put({}));

    if (!response.ok) {
        throw new Error(`Failed to archive product: ${response.statusText}`);
    }
}

export async function getOrganizationProducts(
    entity_type: PaymentTargetType,
    entity_id: string,
    organization_id: string,
    organization_key?: string,
) {
    const response = await fetch(`${API_BASE_URL}/products/${organization_id}?${organization_key ? `organization_key=${organization_key}` : ''}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting organization products.', response);
    }
    const products: GetOrganizationProductResponse[] = await response.json();
    // filter products by entity type and id
    const filteredProducts = products.filter(product => {
        if ('Form' in product.item_details && entity_type === PaymentTargetType.Form) {
            return product.item_details.Form.topic_id.$oid === entity_id;
        } else if ('Paywall' in product.item_details && entity_type === PaymentTargetType.Paywall) {
            return product.item_details.Paywall.topic_id.$oid === entity_id;
        } else if ('Ballot' in product.item_details && entity_type === PaymentTargetType.Ballot) {
            return product.item_details.Ballot.election_id.$oid === entity_id;
        } else if ('ActivatedChallenge' in product.item_details && entity_type === PaymentTargetType.ActivatedChallenge) {
            return product.item_details.ActivatedChallenge.challenge_id.$oid === entity_id;
        }
        return false;
    });

    return filteredProducts;
}

export async function getOrganizationProductsPublic(
    entity_type: PaymentTargetType,
    target_entity_id: string,
    organization_id: string,
) {
    // A missing organization or entity id has no products; skip the request rather than build a broken URL.
    if (!organization_id || !target_entity_id) {
        return [];
    }
    const response = await fetch(`${API_BASE_URL}/products/${organization_id}/public?${entity_type ? `entity_type=${entity_type}&target_entity_id=${target_entity_id}` : ''}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting organization products.', response);
    }
    const products: GetPublicOrganizationProductResponse[] = await response.json();

    // filter products by entity type and id
    const filteredProducts = products.filter(product => {
        if ('Form' in product.item_details && entity_type === PaymentTargetType.Form) {
            return product.item_details.Form.topic_id.$oid === target_entity_id && product.status === 'active';
        } else if ('Paywall' in product.item_details && entity_type === PaymentTargetType.Paywall) {
            return product.item_details.Paywall.topic_id.$oid === target_entity_id && product.status === 'active';
        } else if ('Ballot' in product.item_details && entity_type === PaymentTargetType.Ballot) {
            return product.item_details.Ballot.election_id.$oid === target_entity_id && product.status === 'active';
        } else if ('ActivatedChallenge' in product.item_details && entity_type === PaymentTargetType.ActivatedChallenge) {
            return product.item_details.ActivatedChallenge.challenge_id.$oid === target_entity_id && product.status === 'active';
        }
        return false;
    });

    return filteredProducts;
}


export async function getProductById(
    product_id: string,
): Promise<GetPublicOrganizationProductResponse> {
    const url = `${API_BASE_URL}/product/${product_id}/public`;

    const response = await fetch(url, get());

    if (!response.ok) {
        throw new Error(`Failed to get product: ${response.statusText}`);
    }

    return await response.json();
}

// HELPERS

export function getCurrencySymbol(currencyCode: string): string {
    const symbols: { [key: string]: string } = {
        'usd': '$',
        'eur': '€',
        'gbp': '£',
        'aud': 'A$',
        'cad': 'CA$',
        'mxn': 'MX$',
        'zar': 'R',
    };
    return symbols[currencyCode.toLowerCase()] || currencyCode.toUpperCase();
}

/**
 * Format price with currency symbol
 */
export function formatPrice(amountCents: number, currencyCode: string): string {
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${(amountCents / 100).toFixed(2)} ${currencyCode.toUpperCase()}`;
}

export function getPriceTypeFromProduct(product: GetOrganizationProductResponse | GetPublicOrganizationProductResponse): string {

    // Check if it's a Fixed price structure
    if ('Fixed' in product.pricing_structure) {
        return 'fixed';
    }

    // Check if it's a Recurring price structure
    if ('Recurring' in product.pricing_structure) {
        return 'recurring';
    }

    // Check if it's a Donation price structure
    if ('Donation' in product.pricing_structure) {
        return 'donation';
    }

    return 'unknown';
}

export function getPriceStringFromProduct(product: GetOrganizationProductResponse | GetPublicOrganizationProductResponse | CreateOrganizationProductRequest): string {

    if ('Fixed' in product.pricing_structure) {
        const fixed = (product.pricing_structure as any).Fixed as FixedPriceSettings;
        const symbol = getCurrencySymbol(product.currency_code);
        return (`${symbol}${(fixed.amount_cents / 100).toFixed(2)}`);
    }

    if ('Recurring' in product.pricing_structure) {
        const subscription = (product.pricing_structure as any).Recurring as RecurringPriceSettings;
        const interval = subscription.interval === ProductPeriod.Monthly ? 'month' : subscription.interval === ProductPeriod.Weekly ? 'week' : '';
        const symbol = getCurrencySymbol(product.currency_code);
        return (`${symbol}${(subscription.amount_cents / 100).toFixed(2)} per ${interval}`);
    }

    if ('Donation' in product.pricing_structure) {
        return (`Variable`);
    }

    return `unknown`;
}


export function getPriceCentsFromProduct(product: GetOrganizationProductResponse | GetPublicOrganizationProductResponse | CreateOrganizationProductRequest): number {

    if ('Fixed' in product.pricing_structure) {
        const fixed = (product.pricing_structure as any).Fixed as FixedPriceSettings;
        return (fixed.amount_cents);
    }

    if ('Recurring' in product.pricing_structure) {
        const subscription = (product.pricing_structure as any).Recurring as RecurringPriceSettings;
        return (subscription.amount_cents);
    }

    if ('Donation' in product.pricing_structure) {
        return (0);
    }

    return (0);
}